import React, { useEffect, useRef } from "react";

import {
  useApiErrors,
  useAuth,
  useDisclose,
  useHookForm,
  useMerchantApiMutation,
} from "../../hooks";
import {
  getApiErrorsByFieldName,
  getUserDepartment,
  globalHandlerUtils,
} from "../../utils";
import {
  IChangeTransferPin,
  IViewAuthorization,
} from "../../interfaces-and-types";
import { createTransferPinSchema } from "./validation-schema";
import { View } from "./view";
import { useNavigate } from "react-router-dom";

const CURRENT_PIN_FIELD_NAME = "current_pin";
const NEW_PIN_FIELD_NAME = "new_pin";
const CONFIRM_PIN_FIELD_NAME = "confirm_pin";

export const ChangeTransferPin = () => {
  const navigate = useNavigate();
  const { active_tenant_id, user, staff_departments, setStaffDepartments } =
    useAuth();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = React.useState(false);

  let timeoutId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const activeStaffDept = getUserDepartment(
      active_tenant_id,
      staff_departments
    );

    if (!activeStaffDept?.has_pin) {
      timeoutId.current = setTimeout(
        () =>
          window.confirm(
            // "PIN Setup",
            "You don't have a PIN for this profile yet. Do you want to create a PIN?"
          )
            ? navigate("/settings/create-transfer-pin")
            : navigate("/settings"),
        1500
      );
    }

    return () => timeoutId && clearTimeout(timeoutId.current);
  }, [active_tenant_id]);

  const {
    isLoading: isLoadingChangeTransferPin,
    isSuccess: isSuccessChangeTransferPin,
    isError: isErrorChangeTransferPin,
    data: apiResponse,
    mutate: createTransferPin,
  } = useMerchantApiMutation<IChangeTransferPin, IViewAuthorization>(
    "ChangeTransferPin"
  ).usePostMutation;

  useEffect(() => {
    if (isSuccessChangeTransferPin) {
      // update the dept profile
      const accountWithNewPin = getUserDepartment(
        active_tenant_id,
        staff_departments
      );

      setStaffDepartments([
        ...staff_departments!.filter((d) => d.merchant_id !== active_tenant_id),
        {
          ...accountWithNewPin!,
          has_pin: true,
        },
      ]);

      navigate("/settings");
    }
  }, [isSuccessChangeTransferPin]);

  const initialFormValues: IChangeTransferPin = {
    current_pin: "",
    new_pin: "",
    confirm_pin: "",
  };

  const handleApiSubmit = (data: IChangeTransferPin) => {
    createTransferPin(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const {
    onFormSubmit,
    errors,
    getValues,
    register,
    reset,
    setValue,
    setError,
    control,
    watch,
  } = useHookForm({
    initialFormValues,
    validationSchema: createTransferPinSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();

  const { apiErrors } = useApiErrors();

  // old PIN
  const combinedCurrentPinInputApiErrorMessage =
    getApiErrorsByFieldName(CURRENT_PIN_FIELD_NAME, apiErrors) || "";
  const combinedCurrentPinHookFormErrorMessage =
    errors[CURRENT_PIN_FIELD_NAME] &&
    errors[CURRENT_PIN_FIELD_NAME]?.["message"]?.toString();

  const combinedCurrentPinInputErrors =
    combinedCurrentPinHookFormErrorMessage ||
    combinedCurrentPinInputApiErrorMessage ||
    null;

  const {
    isOpen: isOpenCurrentPinInput,
    onClose: onCloseCurrentPinInput,
    onOpen: onOpenCurrentPinInput,
  } = useDisclose();

  const onCurrentPinSubmit = (oldPin: string): boolean => {
    setValue(CURRENT_PIN_FIELD_NAME, oldPin);

    return true;
  };

  const onCurrentPinCorrect = (): boolean => {
    onCloseCurrentPinInput();
    if (!formValues.new_pin) setTimeout(onOpenNewPinInput, 500);

    return true;
  };

  // enter new PIN
  const pinInputApiErrorMessage =
    getApiErrorsByFieldName(NEW_PIN_FIELD_NAME, apiErrors) || "";
  const hookFormErrorMessage =
    errors[NEW_PIN_FIELD_NAME] &&
    errors[NEW_PIN_FIELD_NAME]?.["message"]?.toString();

  const combinedNewPinInputErrors =
    hookFormErrorMessage || pinInputApiErrorMessage || null;

  const {
    isOpen: isOpenNewPinInput,
    onClose: onCloseNewPinInput,
    onOpen: onOpenNewPinInput,
  } = useDisclose();

  const onNewPinSubmit = (pin: string): boolean => {
    setValue(NEW_PIN_FIELD_NAME, pin);
    return true;
  };

  const onNewPinCorrect = (): boolean => {
    onCloseNewPinInput();

    if (!formValues.confirm_pin) setTimeout(onOpenConfirmPinInput, 500);
    return true;
  };

  // confirm PIN
  const combinedConfirmPinInputApiErrorMessage =
    getApiErrorsByFieldName(CONFIRM_PIN_FIELD_NAME, apiErrors) || "";
  const combinedConfirmPinHookFormErrorMessage =
    errors[CONFIRM_PIN_FIELD_NAME] &&
    errors[CONFIRM_PIN_FIELD_NAME]?.["message"]?.toString();

  const combinedConfirmPinInputErrors =
    combinedConfirmPinHookFormErrorMessage ||
    combinedConfirmPinInputApiErrorMessage ||
    null;

  const {
    isOpen: isOpenConfirmPinInput,
    onClose: onCloseConfirmPinInput,
    onOpen: onOpenConfirmPinInput,
  } = useDisclose();

  const onConfirmPinSubmit = (confirmPin: string): boolean => {
    setValue(CONFIRM_PIN_FIELD_NAME, confirmPin);

    const isPinConfirmMatch = formValues.new_pin === confirmPin;

    // if (!isPinConfirmMatch)
    //   setError(CONFIRM_PIN_FIELD_NAME, {
    //     message: "Pin Confirmation must be the same as PIN",
    //   });
    return isPinConfirmMatch;
  };

  const onConfirmPinCorrect = (): boolean => {
    onCloseConfirmPinInput();
    return true;
  };

  return (
    <View
      isModalOpen
      closeModal={() => navigate("/settings")}
      // confirm PIN
      isOpenCurrentPinInput={isOpenCurrentPinInput}
      onOpenCurrentPinInput={onOpenCurrentPinInput}
      onCloseCurrentPinInput={onCloseCurrentPinInput}
      combinedCurrentPinInputErrors={combinedCurrentPinInputErrors}
      onCurrentPinSubmit={onCurrentPinSubmit}
      onCurrentPinCorrect={onCurrentPinCorrect}
      // create PIN
      isOpenNewPinInput={isOpenNewPinInput}
      onOpenNewPinInput={onOpenNewPinInput}
      onCloseNewPinInput={onCloseNewPinInput}
      combinedNewPinInputErrors={combinedNewPinInputErrors}
      onNewPinSubmit={onNewPinSubmit}
      onNewPinCorrect={onNewPinCorrect}
      // confirm PIN
      isOpenConfirmPinInput={isOpenConfirmPinInput}
      onOpenConfirmPinInput={onOpenConfirmPinInput}
      onCloseConfirmPinInput={onCloseConfirmPinInput}
      combinedConfirmPinInputErrors={combinedConfirmPinInputErrors}
      onConfirmPinSubmit={onConfirmPinSubmit}
      onConfirmPinCorrect={onConfirmPinCorrect}
      // hook form
      formValues={formValues}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
      control={control}
    />
  );
};
